import { useState } from "react";

import { Check, Plus, SlidersHorizontal, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { type FilterDef, type FilterType } from "../api";
import { guessParamType } from "../viz";

/** Sentinel for the "no column" Select option (Radix disallows an empty value). */
const NONE = "__none__";

interface Row {
  param: string;
  label: string;
  type: FilterType;
  default: string;
  options: string;
  column: string;
  /** A column auto-filter the author added here (vs. a {{param}} detected in SQL). */
  auto: boolean;
}

function rowFromDef(param: string, d: FilterDef | undefined, auto: boolean): Row {
  return {
    param,
    label: d?.label ?? "",
    type: d?.type ?? (auto ? "select" : guessParamType(param)),
    default: d?.default ?? "",
    options: (d?.options ?? []).join(", "),
    column: d?.column ?? "",
    auto,
  };
}

/**
 * Define the dashboard's filters. Two kinds, both saved to `config.filters`:
 *
 * - **{{param}} filters** auto-detected from panel SQL — give each a label, input
 *   type, default, and (for select) options; the query executor substitutes the
 *   value before running (pre-aggregation).
 * - **Column auto-filters** ("Add auto-filter") — pick a result column; the
 *   viewer then filters every panel whose data has that column by the chosen
 *   value, with no SQL editing. Define once, applies across the board.
 *
 * A {{param}} filter may also name a column, so it substitutes where the
 * placeholder exists *and* row-filters panels that merely expose the column.
 * Collapsed to a button until opened.
 */
export function FilterDefsForm({
  params,
  defs,
  columns = [],
  onSave,
  saving,
}: {
  params: string[];
  defs: FilterDef[];
  /** Result columns available to target with an auto-filter (from the panels). */
  columns?: string[];
  onSave: (defs: FilterDef[]) => void;
  saving?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>(() => {
    const paramSet = new Set(params);
    const fromParams = params.map((p) => rowFromDef(p, defs.find((x) => x.param === p), false));
    const fromDefs = defs
      .filter((d) => !paramSet.has(d.param))
      .map((d) => rowFromDef(d.param, d, true));
    return [...fromParams, ...fromDefs];
  });

  // Nothing to configure and nothing to add → render nothing.
  if (rows.length === 0 && columns.length === 0) return null;

  const update = (i: number, patch: Partial<Row>) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const addAuto = () =>
    setRows((r) => [
      ...r,
      { param: "", label: "", type: "select", default: "", options: "", column: "", auto: true },
    ]);
  const removeRow = (i: number) => setRows((r) => r.filter((_, idx) => idx !== i));

  const save = () => {
    const byParam = new Map<string, FilterDef>();
    for (const r of rows) {
      const column = r.column && r.column !== NONE ? r.column : null;
      // Auto rows are keyed by their column; {{param}} rows keep their name.
      const param = (r.auto ? column ?? r.param : r.param).trim();
      if (!param) continue; // an auto row with no column chosen — skip
      byParam.set(param, {
        param,
        label: r.label.trim() || null,
        type: r.type,
        default: r.default.trim() || null,
        options: r.type === "select" ? r.options.split(",").map((s) => s.trim()).filter(Boolean) : null,
        column,
      });
    }
    onSave([...byParam.values()]);
    setOpen(false);
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <SlidersHorizontal className="h-3.5 w-3.5" />
        {rows.length > 0 ? `Configure filters (${rows.length})` : "Add dashboard filter"}
      </Button>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <SlidersHorizontal className="h-3.5 w-3.5" />
        Dashboard filters
      </div>
      {rows.map((row, i) => {
        // Offer the available columns plus this row's current one (so edits keep it).
        const colOptions = [...new Set([...columns, ...(row.column ? [row.column] : [])])];
        return (
          <div key={row.auto ? `auto-${i}` : row.param} className="space-y-1.5 border-b border-border pb-2 last:border-0">
            <div className="flex items-center justify-between">
              {row.auto ? (
                <span className="text-[11px] font-semibold text-brand">Auto-filter by column</span>
              ) : (
                <code className="text-[11px] font-semibold text-brand">{`{{${row.param}}}`}</code>
              )}
              {row.auto && (
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="text-muted-foreground hover:text-danger"
                  title="Remove this filter"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Label"
                value={row.label}
                onChange={(e) => update(i, { label: e.target.value })}
                className="h-8"
              />
              <Select value={row.type} onValueChange={(v) => update(i, { type: v as FilterType })}>
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="select">Select</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={row.column || NONE}
                onValueChange={(v) => update(i, { column: v === NONE ? "" : v })}
              >
                <SelectTrigger className="h-8" title="Result column this filter matches rows on">
                  <SelectValue placeholder="Column (auto-filter)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{row.auto ? "— pick a column —" : "No column"}</SelectItem>
                  {colOptions.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Default (optional)"
                type={row.type === "date" ? "date" : "text"}
                value={row.default}
                onChange={(e) => update(i, { default: e.target.value })}
                className="h-8"
              />
              {row.type === "select" && (
                <Input
                  placeholder="Options (comma-sep)"
                  value={row.options}
                  onChange={(e) => update(i, { options: e.target.value })}
                  className="h-8"
                />
              )}
            </div>
          </div>
        );
      })}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={save} disabled={saving}>
          <Check className="h-3.5 w-3.5" />
          Save filters
        </Button>
        <Button variant="outline" size="sm" onClick={addAuto} disabled={columns.length === 0}>
          <Plus className="h-3.5 w-3.5" />
          Add auto-filter
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        A <span className="font-medium">column auto-filter</span> applies to every panel whose data
        includes that column — no query edits. A <code className="text-brand">{`{{param}}`}</code>{" "}
        filter substitutes into the panels whose SQL references it.
      </p>
    </div>
  );
}
